import axios from "axios";
import { format } from "date-fns";
import React, { useContext, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { utils, writeFile } from "xlsx";
import { UserContext } from "../../Context/UserContextAPI";
import Loading from "../../utils/Loader/Loading";
import "../ProcessAndProcedure/MedicalReports/MedicalReports.scss";
import UserSearchForm from "./../../components/Users/UserSearchForm";
import COllectionTable from "./components/CollectionTable";
import useIsOneIntroducer from "../../hooks/useIsOneIntroducer";
const CollectionReport = () => {
  const { accessPerm } = useContext(UserContext);
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterData, setFilterData] = useState({
    fromDate: new Date().toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0],
    viewOptions: "all",
  });

  // custom hooks
  const isOneIntroducer = useIsOneIntroducer();

  // Function to handle key press on a row

  const downloadExcel = () => {
    // Extract headers from the table
    const headers = [
      "SL", // Serial Number first
      "Date",
      "Reg. No",
      "Passenger",
      "Passport No",

      ...(!isOneIntroducer ? ["Agency"] : []),
      "Country", // Add Country column
      "Test/Ref By", // Add Test/Ref By column
      "Received",
      "Net Amount",
      "Balance",
      "Dues",
      "Paid",
      "Remarks",
    ];

    // Prepare data for the worksheet
    const formattedData = usersData
      .map((user, index) => {
        // filter out if user is not active
        if (!user?.is_active) {
          return null;
        }

        return {
          SL: index + 1, // Serial number
          Date: format(new Date(user?.createdAt), "dd-MM-yyyy"),
          "Reg. No": `${user?.reg_prefix || ""}${user?.registration_no || ""}`,
          Passenger: user?.f_name || "",
          ...(!isOneIntroducer && {
            Agency: user?.userDetail?.introducer_user?.f_name || "",
          }),
          "Passport No": user?.passport_no || "",

          Country: user?.userDetail?.country?.name || "", // Get Country from userDetails
          "Test/Ref By":
            user?.role_id === 5
              ? user?.userTest
                  ?.map((test) => test?.investigation?.code)
                  .join(", ")
              : user?.userDetail?.package?.name || "", // Assign Test/Ref By value
          Received: user?.userDetail?.user_payment?.paid_amount || 0, // Rename to Paid
          "Net Amount": user?.userDetail?.user_payment?.net_amount || 0,
          Balance: user?.userDetail?.user_payment?.commission || 0,
          Dues: user?.userDetail?.user_payment?.due || 0,
          Paid: user?.userDetail?.user_payment?.paid || 0,
          Remarks: user?.userDetail?.user_payment?.remarks || "",
        };
      })
      .filter(Boolean);

    // Calculate totals for the relevant columns
    const totalReceived = formattedData.reduce(
      (sum, row) => sum + row["Received"],
      0
    );
    const totalPaid = formattedData.reduce((sum, row) => sum + row["Paid"], 0);
    const totalCommission = formattedData.reduce(
      (sum, row) => sum + row["Balance"],
      0
    );
    const totalNetAmount = formattedData.reduce(
      (sum, row) => sum + row["Net Amount"],
      0
    );
    const totalDues = formattedData.reduce((sum, row) => sum + row["Dues"], 0);

    // Add totals row to formattedData with the total under Test/Ref By
    formattedData.push({
      SL: "", // Leave SL blank for the total row
      "Reg. No": "",
      Passenger: "",
      ...(!isOneIntroducer && {
        Agency: "",
      }),
      Country: "", // Leave Country blank for the total row
      "Test/Ref By": "Total", // Add Total under Test/Ref By
      Received: totalReceived, // Use totalReceived
      "Net Amount": totalNetAmount,
      Balance: totalCommission,
      Dues: totalDues,
      Paid: totalPaid, // Use totalPaid
      Remarks: "",
    });

    // Create worksheet from the formatted data
    const worksheet = utils.json_to_sheet(formattedData, { header: headers });

    // Create a new workbook and append the worksheet
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Collection Data");

    // Generate a file name
    const fileName = `Collection_Report_${format(new Date(), "yyyyMMdd")}.xlsx`;

    // Export the workbook
    writeFile(workbook, fileName);
  };

  const saveCollection = async (data) => {
    try {
      // Validate input data
      if (!Array.isArray(usersData)) {
        throw new Error("Invalid users data");
      }

      const hasChangedData = usersData?.filter(
        (user) => user?.userDetail?.user_payment?.hasLocalDataChanged === true
      );

      if (!hasChangedData.length) {
        toast.error("No data Changed");
        return; // Or handle empty data case appropriately
      }
      console.log("hasChangedData", hasChangedData);

      const sortChangedValue = hasChangedData.map((changedData) => ({
        id: changedData?.userDetail?.user_id,
        commission:
          Number(changedData?.userDetail?.user_payment?.commission) || 0,
        dues: Number(changedData?.userDetail?.user_payment?.due) || 0,
        remarks: changedData?.userDetail?.user_payment?.remarks || "",
        paid: Number(changedData?.userDetail?.user_payment?.paid) || 0,
        receivedAmount:
          Number(changedData?.userDetail?.user_payment?.paid_amount) || 0,
        netAmount:
          Number(changedData?.userDetail?.user_payment?.net_amount) || 0,
        hasChanged: changedData?.userDetail?.user_payment?.hasChanged,
      }));

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/users/collection/payment`,
        sortChangedValue,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
          },
        }
      );

      if (response?.data?.status === "OK") {
        toast.success(response.data?.data);
      }

      return response.data;
    } catch (error) {
      console.log(error);
      const errorMessage =
        error?.response?.data?.data?.error ||
        "An error occurred while saving collection";
      toast.error(errorMessage);
    }
  };
  // Get Specific date users-------
  const methodFilterPatientsDetails = async (filterData) => {
    try {
      setLoading(true);
      const query = new URLSearchParams(filterData).toString();
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v2/users/search-patients-for-collection?${query}`,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${localStorage?.getItem("jwtToken")}`,
          },
        }
      );
      const data = response?.data?.data;
      // console.log("data", data);
      if (response?.data?.status === "OK") {
        setUsersData(data || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await methodFilterPatientsDetails({
          fromDate: new Date(),
          toDate: new Date(),
          viewOptions: "all",
        });
      } catch (error) {
        console.log(error);
      }
    })();
    return () => {
      // console.log("unmount");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // console.log("usersData", usersData);

  return (
    <>
      <div className="card medical-report-container">
        <div className="card-body">
          <div className="rounded">
            <div className="card shadow-none mb-3">
              <div className="border p-3 rounded">
                <h6
                  className="mb-0 text-uppercase fw-bold"
                  style={{ color: "red" }}
                >
                  Collection Report
                </h6>

                <div className="d-flex flex-wrap gap-2  mt-0">
                  <UserSearchForm
                    userSearchHandler={(val) => {
                      methodFilterPatientsDetails(val);
                      setFilterData(val);
                    }}
                    fromCollectionReport={true}
                    inlineButtonComponent={
                      <>
                        {accessPerm(76, 2) && (
                          <button
                            type="submit"
                            className="btn bg-success px-4 border border-3 text-white"
                            onClick={saveCollection}
                          >
                            Save
                          </button>
                        )}
                        {accessPerm(76, 23) && (
                          <>
                            <button
                              type="button"
                              className="btn bg-danger px-4 border border-3 text-white"
                              onClick={() => {
                                const queryString = new URLSearchParams(
                                  filterData
                                ).toString();
                                const url = `/dashboard/medical-collection/print?${queryString}`;

                                window.open(url, "_blank");
                              }}
                            >
                              PDF
                            </button>
                            <button
                              type="button"
                              onClick={downloadExcel}
                              className="btn btn-primary px-4 border border-3"
                            >
                              Excel
                            </button>
                          </>
                        )}
                      </>
                    }
                  />
                </div>
              </div>
            </div>
            {loading ? (
              <Loading />
            ) : (
              <>
                <COllectionTable
                  usersData={usersData}
                  setUsersData={setUsersData}
                  refreshData={() => {
                    methodFilterPatientsDetails(filterData);
                  }}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
const MemoizedMyComponent = React.memo(CollectionReport);
export default MemoizedMyComponent;
